import React, { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Stats = () => {
  const [habits, setHabits] = useState([]);
  const [completionHistory, setCompletionHistory] = useState([]);

  useEffect(() => {
    // Load habits from localStorage
    const savedHabits = localStorage.getItem('habits');
    if (savedHabits) {
      setHabits(JSON.parse(savedHabits));
    }

    // Load completion history from localStorage
    const savedHistory = localStorage.getItem('completionHistory');
    if (savedHistory) {
      setCompletionHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Prepare data for bar chart (streak lengths)
  const barChartData = {
    labels: habits.map(habit => habit.name),
    datasets: [
      {
        label: 'Current Streak (days)',
        data: habits.map(habit => habit.streak || 0),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  // Prepare data for line chart (daily completions)
  const getLast7Days = () => {
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const last7Days = getLast7Days();
  const lineChartData = {
    labels: last7Days.map(date => {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Completed Habits',
        data: last7Days.map(date => {
          const dayData = completionHistory.find(h => h.date === date);
          return dayData ? dayData.completedCount : 0;
        }),
        borderColor: 'rgba(34, 197, 94, 1)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Habit Analytics',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const totalHabits = habits.length;
  const completedToday = habits.filter(habit => habit.completedToday).length;
  const totalStreaks = habits.reduce((sum, habit) => sum + (habit.streak || 0), 0);
  const averageStreak = totalHabits > 0 ? (totalStreaks / totalHabits).toFixed(1) : 0;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          📊 Habit Statistics
        </h2>
        <p className="text-gray-600 text-lg font-medium">
          Track your progress and build momentum with detailed analytics.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{totalHabits}</div>
            <div className="text-gray-600 font-medium">Total Habits</div>
          </div>
        </div>
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{completedToday}</div>
            <div className="text-gray-600 font-medium">Completed Today</div>
          </div>
        </div>
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{totalStreaks}</div>
            <div className="text-gray-600 font-medium">Total Streak Days</div>
          </div>
        </div>
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{averageStreak}</div>
            <div className="text-gray-600 font-medium">Avg. Streak</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Bar Chart */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Current Streaks</h3>
          <Bar data={barChartData} options={chartOptions} />
        </div>

        {/* Line Chart */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/20">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Daily Completions (Last 7 Days)</h3>
          <Line data={lineChartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default Stats;
